import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, toBaseCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Landmark, Plus, ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, ListOrdered, Trash2 } from "lucide-react";
import { TPAList } from "../components/banks/TPAList";
import { TPAFormRows } from "../components/banks/TPAFormRows";

const MOV_LABELS = {
  deposit: { label: "Depósito", cls: "bg-emerald-500/20 text-emerald-600" },
  withdrawal: { label: "Levantamento", cls: "bg-red-500/20 text-red-600" },
  transfer_in: { label: "Transf. Entrada", cls: "bg-blue-500/20 text-blue-600" },
  transfer_out: { label: "Transf. Saída", cls: "bg-amber-500/20 text-amber-600" },
  tpa_sale: { label: "Venda TPA", cls: "bg-blue-500/20 text-blue-600" },
  tpa_reversal: { label: "Estorno TPA (anulação)", cls: "bg-red-500/20 text-red-600" },
};

export default function BanksPage() {
  const { token, isAdmin } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountModal, setAccountModal] = useState(false);
  const [accForm, setAccForm] = useState({ name: "", bank_name: "", iban: "", initial_balance: "", tpas: [] });
  const [movModal, setMovModal] = useState(false);
  const [selectedAcc, setSelectedAcc] = useState(null);
  const [movForm, setMovForm] = useState({ type: "deposit", amount: "", description: "" });
  const [movements, setMovements] = useState([]);
  const [movListModal, setMovListModal] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [transferForm, setTransferForm] = useState({ from_account_id: "", to_account_id: "", amount: "", description: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAccounts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/banks`, { headers, withCredentials: true });
      setAccounts(res.data);
    } catch { toast.error("Erro ao carregar contas bancárias"); }
    finally { setLoading(false); }
  };

  const createAccount = async (e) => {
    e.preventDefault();
    if (accForm.tpas.some((t) => !t.name.trim())) return toast.error("Dê um nome a cada TPA ou remova a linha");
    try {
      await axios.post(`${API_URL}/banks`, {
        ...accForm,
        initial_balance: toBaseCurrency(accForm.initial_balance),
        tpas: accForm.tpas.map((t) => ({ name: t.name.trim(), serial: t.serial.trim() || null })),
      }, { headers, withCredentials: true });
      toast.success(accForm.tpas.length ? `Conta criada com ${accForm.tpas.length} TPA` : "Conta bancária criada");
      setAccountModal(false); setAccForm({ name: "", bank_name: "", iban: "", initial_balance: "", tpas: [] });
      fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar conta"); }
  };

  const deleteAccount = async (acc) => {
    if (!window.confirm(`Remover conta "${acc.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/banks/${acc.account_id}`, { headers, withCredentials: true });
      toast.success("Conta removida");
      fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover"); }
  };

  const createMovement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/banks/${selectedAcc.account_id}/movements`, {
        ...movForm,
        amount: toBaseCurrency(movForm.amount),
      }, { headers, withCredentials: true });
      toast.success("Movimento registado");
      setMovModal(false); setMovForm({ type: "deposit", amount: "", description: "" });
      fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro no movimento"); }
  };

  const openMovements = async (acc) => {
    try {
      const res = await axios.get(`${API_URL}/banks/${acc.account_id}/movements`, { headers, withCredentials: true });
      setMovements(res.data);
      setSelectedAcc(acc);
      setMovListModal(true);
    } catch { toast.error("Erro ao carregar movimentos"); }
  };

  const doTransfer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/banks/transfer`, {
        ...transferForm,
        amount: toBaseCurrency(transferForm.amount),
      }, { headers, withCredentials: true });
      toast.success("Transferência registada");
      setTransferModal(false); setTransferForm({ from_account_id: "", to_account_id: "", amount: "", description: "" });
      fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro na transferência"); }
  };

  const totalBalance = accounts.reduce((s, a) => s + (a.balance || 0), 0);

  return (
    <div className="space-y-6" data-testid="banks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Bancos</h1>
          <p className="text-muted-foreground">Contas bancárias, depósitos, levantamentos e transferências</p>
        </div>
        <div className="flex gap-2">
          {accounts.length >= 2 && (
            <Button variant="outline" onClick={() => setTransferModal(true)} data-testid="transfer-btn">
              <ArrowLeftRight className="w-4 h-4 mr-2" /> Transferir
            </Button>
          )}
          <Button onClick={() => setAccountModal(true)} data-testid="add-bank-btn">
            <Plus className="w-4 h-4 mr-2" /> Nova Conta
          </Button>
        </div>
      </div>

      <Card><CardContent className="p-4">
        <p className="text-xs text-muted-foreground">Saldo Total em Bancos</p>
        <p className="text-2xl font-bold font-mono" data-testid="total-bank-balance">{formatCurrency(totalBalance)}</p>
      </CardContent></Card>

      {loading ? <p className="text-muted-foreground">A carregar...</p> :
        accounts.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <Landmark className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Nenhuma conta bancária registada
          </CardContent></Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {accounts.map((acc) => (
              <Card key={acc.account_id} data-testid={`bank-card-${acc.account_id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{acc.name}</p>
                      {acc.bank_name && <p className="text-xs text-muted-foreground">{acc.bank_name}</p>}
                      {acc.iban && <p className="text-xs text-muted-foreground font-mono">{acc.iban}</p>}
                    </div>
                    <Landmark className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-xl font-bold font-mono">{formatCurrency(acc.balance)}</p>
                  <TPAList account={acc} headers={headers} onChanged={fetchAccounts} />
                  <div className="flex gap-2 pt-1 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => { setSelectedAcc(acc); setMovForm({ type: "deposit", amount: "", description: "" }); setMovModal(true); }} data-testid={`deposit-btn-${acc.account_id}`}>
                      <ArrowDownToLine className="w-4 h-4 mr-1" /> Depósito
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setSelectedAcc(acc); setMovForm({ type: "withdrawal", amount: "", description: "" }); setMovModal(true); }}>
                      <ArrowUpFromLine className="w-4 h-4 mr-1" /> Levantar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openMovements(acc)} data-testid={`movements-btn-${acc.account_id}`}>
                      <ListOrdered className="w-4 h-4" />
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteAccount(acc)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={accountModal} onOpenChange={setAccountModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nova Conta Bancária</DialogTitle></DialogHeader>
          <form onSubmit={createAccount} className="space-y-3">
            <div className="space-y-1">
              <Label>Nome da Conta *</Label>
              <Input required value={accForm.name} onChange={(e) => setAccForm({ ...accForm, name: e.target.value })} placeholder="Ex: Conta Principal" data-testid="bank-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Banco</Label>
              <Input value={accForm.bank_name} onChange={(e) => setAccForm({ ...accForm, bank_name: e.target.value })} placeholder="Ex: BAI" />
            </div>
            <div className="space-y-1">
              <Label>IBAN</Label>
              <Input value={accForm.iban} onChange={(e) => setAccForm({ ...accForm, iban: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Saldo Inicial</Label>
              <Input type="number" step="0.01" min="0" value={accForm.initial_balance} onChange={(e) => setAccForm({ ...accForm, initial_balance: e.target.value })} data-testid="bank-initial-balance-input" />
            </div>
            <TPAFormRows tpas={accForm.tpas} onChange={(tpas) => setAccForm({ ...accForm, tpas })} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAccountModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-bank-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={movModal} onOpenChange={setMovModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{movForm.type === "deposit" ? "Depósito" : "Levantamento"} — {selectedAcc?.name}</DialogTitle></DialogHeader>
          <form onSubmit={createMovement} className="space-y-3">
            <div className="space-y-1">
              <Label>Valor *</Label>
              <Input type="number" step="0.01" min="0.01" required value={movForm.amount} onChange={(e) => setMovForm({ ...movForm, amount: e.target.value })} data-testid="movement-amount-input" />
            </div>
            <div className="space-y-1">
              <Label>Descrição</Label>
              <Input value={movForm.description} onChange={(e) => setMovForm({ ...movForm, description: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMovModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-movement-btn">Registar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={movListModal} onOpenChange={setMovListModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Movimentos — {selectedAcc?.name}</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {movements.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Sem movimentos</p>}
            {movements.map((m) => (
              <div key={m.movement_id} className="flex justify-between items-center p-2 rounded border text-sm">
                <div>
                  <Badge className={MOV_LABELS[m.type]?.cls}>{MOV_LABELS[m.type]?.label || m.type}</Badge>{m.reversed && <Badge variant="outline" className="ml-1 text-[10px]">estornado</Badge>}
                  <p className="text-xs text-muted-foreground mt-1">{m.description} · {formatDate(m.created_at)}</p>
                </div>
                <p className="font-mono font-bold">{["deposit", "transfer_in", "tpa_sale"].includes(m.type) ? "+" : "-"}{formatCurrency(m.amount)}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={transferModal} onOpenChange={setTransferModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Transferência entre Contas</DialogTitle></DialogHeader>
          <form onSubmit={doTransfer} className="space-y-3">
            <div className="space-y-1">
              <Label>De *</Label>
              <Select value={transferForm.from_account_id} onValueChange={(v) => setTransferForm({ ...transferForm, from_account_id: v })}>
                <SelectTrigger data-testid="transfer-from-select"><SelectValue placeholder="Conta origem" /></SelectTrigger>
                <SelectContent>{accounts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.name} ({formatCurrency(a.balance)})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Para *</Label>
              <Select value={transferForm.to_account_id} onValueChange={(v) => setTransferForm({ ...transferForm, to_account_id: v })}>
                <SelectTrigger data-testid="transfer-to-select"><SelectValue placeholder="Conta destino" /></SelectTrigger>
                <SelectContent>{accounts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Valor *</Label>
              <Input type="number" step="0.01" min="0.01" required value={transferForm.amount} onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })} data-testid="transfer-amount-input" />
            </div>
            <div className="space-y-1">
              <Label>Descrição</Label>
              <Input value={transferForm.description} onChange={(e) => setTransferForm({ ...transferForm, description: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTransferModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="confirm-transfer-btn">Transferir</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
